import type { InferResponseType } from 'hono/client'
import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { store } from '.'
import type { HonoClient } from './api'

const SESSION_TOKEN_KEY = 'dp_sessionToken'
const TENANT_KEY = 'dp_tenant'

type SelectTenantTenant = InferResponseType<
  HonoClient['portal-auth']['driver']['select-tenant']['$post']
>['tenant']

// `type` is optional because only the portal-auth endpoints carry it. The magic
// login and onboarding-join paths mint their tenant from a different query that
// returns a `company` object instead, so requiring it would force those callers
// to invent a value they do not have.
export type TenantInfo = Omit<SelectTenantTenant, 'type'> & {
  type?: SelectTenantTenant['type']
}

export const sessionTokenAtom = atomWithStorage<string | null>(SESSION_TOKEN_KEY, null, undefined, {
  getOnInit: true,
})

export const tenantAtom = atomWithStorage<TenantInfo | null>(TENANT_KEY, null, undefined, {
  getOnInit: true,
})

export const getSessionToken = () => store.get(sessionTokenAtom)
export const setSessionToken = (token: string) => store.set(sessionTokenAtom, token)
export const destroySessionToken = () => {
  store.set(sessionTokenAtom, null)
}

export const getTenant = () => store.get(tenantAtom)
export const setTenant = (tenant: TenantInfo) => store.set(tenantAtom, tenant)
export const clearTenant = () => {
  store.set(tenantAtom, null)
}

const isSwitchingTenantAtom = atom(false)
export const setIsSwitchingTenant = (value: boolean) => store.set(isSwitchingTenantAtom, value)

// Holds the tempToken + tenants list returned by verify-code when the user
// belongs to multiple tenants and hasn't picked one yet. Cleared after
// select-tenant completes (or when the user backs out to /login).
type PendingTenantOption = {
  id: string
  name: string
  slug: string
  driverStatus?: string | null
}
type PendingTenantSelection = {
  tempToken: string
  tenants: PendingTenantOption[]
}
export const pendingTenantSelectionAtom = atom<PendingTenantSelection | null>(null)
export const setPendingTenantSelection = (value: PendingTenantSelection | null) =>
  store.set(pendingTenantSelectionAtom, value)
export const getPendingTenantSelection = () => store.get(pendingTenantSelectionAtom)
